import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Pencil } from "lucide-react";
import { api, formatApiError } from "../../api";
import { StatusPill } from "../../components/common/StatusPill";
import { ErrorState } from "../../components/common/ErrorState";
import { ModuleTogglesPanel } from "../../components/control/ModuleTogglesPanel";
import { SeatLicensePanel } from "../../components/control/SeatLicensePanel";
import { SuspendRestorePanel } from "../../components/control/SuspendRestorePanel";
import { GymSuspendPanel } from "../../components/control/GymSuspendPanel";
import { GymNotesPanel } from "../../components/control/GymNotesPanel";
import { GymOwnerPanel } from "../../components/control/GymOwnerPanel";
import { BillingEditPanel } from "../../components/control/BillingEditPanel";
import { AuditLogPanel } from "../../components/control/AuditLogPanel";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "../../components/ui/sonner";
import { CONTROL } from "../../constants/testIds";
import { useKairooData } from "../../contexts/KairooDataContext";
import { fadeUp } from "../../lib/motion";

// UX-CP-01 §3 SCR-P08-12 "Gym detail — Overview / Modules / Seats & License / Suspend" tabs.
export default function GymDetail() {
  const { orgId } = useParams();
  const { me } = useKairooData();
  const reducedMotion = useReducedMotion();
  const permissions = me?.kairoo_permissions || [];
  const canEditName = permissions.includes("provision");
  const queryClient = useQueryClient();
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  const gymQ = useQuery({
    queryKey: ["control-gym-detail", orgId],
    queryFn: async () => (await api.get(`/control/gyms/${orgId}`)).data,
  });

  const nameMutation = useMutation({
    mutationFn: () => api.patch(`/control/gyms/${orgId}/name`, { display_name: nameDraft }),
    onSuccess: () => {
      toast.success("Gym name updated.");
      queryClient.invalidateQueries({ queryKey: ["control-gym-detail", orgId] });
      setEditingName(false);
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  useEffect(() => { setNameDraft(gymQ.data?.display_name || ""); }, [gymQ.data?.display_name]);

  if (gymQ.isError) return <ErrorState error={gymQ.error} onRetry={() => gymQ.refetch()} title="Couldn't load this gym" />;
  if (gymQ.isLoading) return <div className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>Loading gym…</div>;

  const gym = gymQ.data;

  return (
    <div data-testid={CONTROL.gymDetailPage} className="space-y-6 max-w-4xl pb-8">
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center gap-3">
        {editingName ? (
          <>
            <Input
              data-testid={CONTROL.gymNameInput}
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              className="text-3xl font-bold h-auto py-1 max-w-md"
              style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}
              autoFocus
            />
            <Button data-testid={CONTROL.gymNameSaveButton} size="sm" disabled={nameMutation.isPending || !nameDraft.trim()} onClick={() => nameMutation.mutate()} className="text-white" style={{ background: "var(--scarlet-accent)" }}>
              Save
            </Button>
            <Button data-testid={CONTROL.gymNameCancelButton} size="sm" variant="outline" onClick={() => { setEditingName(false); setNameDraft(gym.display_name); }}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>{gym.display_name}</h1>
            {canEditName && (
              <button data-testid={CONTROL.gymNameEditButton} onClick={() => setEditingName(true)} aria-label="Edit gym name" style={{ color: "var(--scarlet-text-secondary)" }}>
                <Pencil size={16} />
              </button>
            )}
          </>
        )}
        <StatusPill status={gym.status === "suspended" || gym.status === "deleted" ? "suspended" : (gym.license?.state === "read_only" ? "suspended" : (gym.subscription?.status === "trial" ? "trial" : "active"))} label={gym.status === "suspended" ? "Suspended" : gym.status === "deleted" ? "Deleted" : undefined} />
      </motion.div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid={CONTROL.tabOverview}>Overview</TabsTrigger>
          <TabsTrigger value="modules" data-testid={CONTROL.tabModules}>Modules</TabsTrigger>
          <TabsTrigger value="seats" data-testid={CONTROL.tabSeatsLicense}>Seats & License</TabsTrigger>
          <TabsTrigger value="suspend" data-testid={CONTROL.tabSuspend}>Billing / Suspend</TabsTrigger>
          <TabsTrigger value="audit" data-testid={CONTROL.tabAudit}>Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-3 space-y-4">
          <GymOwnerPanel orgId={orgId} owner={gym.owner} canEdit={permissions.includes("provision")} />
          <div className="solid-surface rounded-[14px] p-5 space-y-3">
            <Field label="Plan tier" value={gym.subscription?.plan_tier || "—"} />
            <Field label="Billing amount" value={gym.subscription?.billing_amount != null ? `₹${(gym.subscription.billing_amount / 100).toLocaleString("en-IN")}` : "Not set"} />
            <Field label="Billing cycle" value={gym.subscription?.billing_cycle || "—"} />
            <Field label="Next billing date" value={gym.subscription?.next_billing_date || "—"} />
            <Field label="Dunning state" value={gym.subscription?.dunning_state || "none"} />
            <Field label="Seats used" value={`${gym.seats_used} / ${gym.license?.seat_limit ?? "—"}`} />
            <Field label="Branches" value={gym.branches.map((b) => b.name).join(", ") || "—"} />
          </div>
          <GymNotesPanel orgId={orgId} notes={gym.internal_notes} canEdit={permissions.includes("provision")} />
        </TabsContent>

        <TabsContent value="modules" className="mt-3">
          <ModuleTogglesPanel orgId={orgId} entitlements={gym.entitlements} canEdit={permissions.includes("provision")} />
        </TabsContent>

        <TabsContent value="seats" className="mt-3">
          <SeatLicensePanel orgId={orgId} license={gym.license} seatsUsed={gym.seats_used} canEdit={permissions.includes("billing")} />
        </TabsContent>

        <TabsContent value="suspend" className="mt-3 space-y-4">
          <BillingEditPanel orgId={orgId} subscription={gym.subscription} canEdit={permissions.includes("billing")} />
          <SuspendRestorePanel orgId={orgId} displayName={gym.display_name} licenseState={gym.license?.state} canEdit={permissions.includes("billing")} />
          <GymSuspendPanel orgId={orgId} displayName={gym.display_name} status={gym.status} suspension={gym.suspension} canEdit={permissions.includes("provision")} />
        </TabsContent>

        <TabsContent value="audit" className="mt-3">
          <AuditLogPanel orgId={orgId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>{label}</span>
      <span className="text-sm font-medium capitalize" style={{ color: "var(--scarlet-text)" }}>{value}</span>
    </div>
  );
}
